import {forwardRef, type FocusEvent, type ReactNode} from "react";
import {useTranslation} from "react-i18next";
import {FormLabel} from "./FormLabel";
import {Checkbox} from "./Checkbox";
import classes from "./form-elements.module.css";

/** One run of the description as delivered by dokumentgenerator. */
export interface OptionalTextSegment {
    text: string;
    /** True when the run is an unfilled variable's placeholder name. */
    placeholder: boolean;
}

interface OptionalFormProps {
    checked: boolean;
    hasFocus: boolean;
    id: string;
    /** The editor's curated label (`data.name`); may be empty. */
    keyword: string;
    /** Plain-text description of the block, as its first line renders in the preview. */
    text: string;
    /** Same content as `text`, split so placeholder runs can be styled. */
    textSegments?: OptionalTextSegment[];
    heading?: string;
    onChange?: () => void;
    onFocus?: (e: FocusEvent) => void;
    onBlur?: (e: FocusEvent) => void;
}

export const OptionalForm = forwardRef<HTMLLabelElement, OptionalFormProps>(
    (
        {
            checked,
            hasFocus,
            id,
            keyword,
            text,
            textSegments,
            heading,
            onChange,
            onFocus,
            onBlur,
        },
        ref
    ) => {
        const {t} = useTranslation();
        const sectionHeading =
            heading ||
            t("DocumentGenerator.OptionalContent", "Valgfritt innhold");

        // Curated editor label wins; else the block's first line as the
        // preview renders it, with unfilled variables styled as placeholders;
        // else a generic fallback so the paragraph is never empty.
        let description: ReactNode;
        let fullText: string;
        if (keyword) {
            description = fullText = keyword;
        } else if (textSegments?.length) {
            fullText =
                text || textSegments.map((segment) => segment.text).join("");
            description = textSegments.map((segment, index) =>
                segment.placeholder ? (
                    <span key={index} className={classes.optionalPlaceholder}>
                        {segment.text}
                    </span>
                ) : (
                    segment.text
                )
            );
        } else if (text) {
            description = fullText = text;
        } else {
            description = fullText = t("DocumentGenerator.OptionalSection", {
                defaultValue: "Valgfritt avsnitt",
            });
        }

        return (
            <FormLabel
                value={sectionHeading}
                active={hasFocus}
                ref={ref}
                className={classes.optionalLabel}
                onFocus={onFocus}
                onBlur={onBlur}
                data-pw="dg-optional"
            >
                <Checkbox
                    checked={checked}
                    onChange={onChange}
                    id={id}
                    className={classes.optionalCheckbox}
                />
                <p className={classes.optionalText} title={fullText}>
                    {description}
                </p>
            </FormLabel>
        );
    }
);

OptionalForm.displayName = "OptionalForm";
